<template>
    <div>
    <div class="pdf-viewer-iframe-wrapper" :class="{ 'pdf-viewer-resizing': isResizing }">
        <iframe width="100%" :style="getHeight" :src="url" allowfullscreen frameborder="0" scrolling="no" :id="iframeId">{{ $t('notSupportPDF') }}</iframe>
        <v-btn
            v-if="showSaveButton && !isResizing"
            @click="saveFileDocument"
            class="save-file-btn mx-2"
            fab
            large
            color="success"
            :loading="loading"
            :disabled="loading"
        >
            <v-icon>mdi-content-save</v-icon>
        </v-btn>
    </div>
    <div v-if="isResizing" :style="getHeight" class="pdf-viewer-resizing">
        <v-progress-circular
            indeterminate
            :size="50"
            :width="5"
            color="secondary"
        ></v-progress-circular>
    </div>
</div>
</template>
<script>
export default {
    props: {
        height: {
            type: [String, Number],
        },
        hideDownload: {
            type: Boolean,
            default: true
        },
        saveDocumentChanges: {
            type: Boolean,
            default: false
        },
        isResizing: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            file: '',
            iframeId: `pdf-viewer-${this._uid}`,
            pdfModifiedFileBytes: false,
            pdfEventListeners: [],
            pdfBytesModifiedListener: null,
            loading: false
        };
    },
    computed: {
        url() {
            return `/js/pdf/web/viewer.html?file=${this.file}`;
        },
        getHeight() {
            if (this.height) return `height: ${this.height}px`;
            else return 'height: ' + (window.innerHeight - 50) + 'px'; //50px header
        },
        showSaveButton() {
            return this.saveDocumentChanges && this.pdfModifiedFileBytes;
        }
    },
    methods: {
        load(file) {
            this.file = file;
            this.resetPdfViewerState();
            this.configurePDFViewer();
        },
        resetPdfViewerState() {
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
                if (key.includes('pdfjs') || key.includes('PDFViewer')) {
                    localStorage.removeItem(key);
                }
            });
        },
        reload() {
            const iframe = document.getElementById(this.iframeId);
            if (!iframe) return;
            iframe.contentWindow.location.reload();
        },
        configurePDFViewer() {
            let langCode = this.$store.getters['account/user'].CultureCode;
            if (langCode === 'es') {
                langCode = 'es-ES';
            } else if (langCode === 'pt') {
                langCode = 'pt-PT';
            }

            const iframe = document.getElementById(this.iframeId);
            if (!iframe) return;

            document.addEventListener('webviewerloaded', () => {
                if (iframe.contentWindow) {
                    const PDFViewerApplicationOptions = iframe.contentWindow['PDFViewerApplicationOptions'];
                    PDFViewerApplicationOptions.set('localeProperties', { lang: langCode });
                }
            }, { once: true });

            iframe.onload = () => {
                const PDFViewerApplication = iframe.contentWindow['PDFViewerApplication'];
                PDFViewerApplication.initializedPromise.then(() => {
                    PDFViewerApplication.appConfig.secondaryToolbar.downloadButton.hidden = this.hideDownload;
                    PDFViewerApplication.appConfig.secondaryToolbar.printButton.hidden = true;
                    PDFViewerApplication.appConfig.secondaryToolbar.openFileButton.hidden = true;
                    PDFViewerApplication.appConfig.toolbar.download.hidden = this.hideDownload;
                    PDFViewerApplication.appConfig.toolbar.print.hidden = true;
                    PDFViewerApplication.appConfig.toolbar.editorInkButton.hidden = !this.saveDocumentChanges;
                    PDFViewerApplication.appConfig.toolbar.editorFreeTextButton.hidden = !this.saveDocumentChanges;
                    PDFViewerApplication.appConfig.toolbar.editorStampButton.hidden = !this.saveDocumentChanges;

                    if (!this.saveDocumentChanges) {
                        return;
                    }

                    this.removePdfEventListeners();
                    this.pdfModifiedFileBytes = false;
                    this.loading = false;

                    this.setupPdfModificationDetection(PDFViewerApplication);

                    PDFViewerApplication.eventBus.on('annotationeditorparamschanged', () => {
                        this.setupPdfModificationDetection(PDFViewerApplication);
                    });
                });
            };
        },
        setupPdfModificationDetection(PDFViewerApplication) {
            this.pdfBytesModifiedListener = (pdfEditorOption) => {
                const pdfDocument = PDFViewerApplication.pdfDocument;
                if (!pdfDocument) {
                    return;
                }
                const annotationStorage = pdfDocument.annotationStorage;
                this.pdfModifiedFileBytes = annotationStorage && (annotationStorage.size > 0 || pdfEditorOption.source.hasSelection);
            };
            const events = [
                'annotationeditorparamschanged',
                'annotationlayerrendered',
                'editingaction',
            ];

            events.forEach(event => {
                PDFViewerApplication.eventBus.on(event, this.pdfBytesModifiedListener);
            });

            const formFields = PDFViewerApplication.pdfViewer.viewer.querySelectorAll('input, textarea, select');
            if (formFields.length === 0) {
                return;
            }
            formFields.forEach(field => {
                field.addEventListener('input', this.pdfBytesModifiedListener);
                field.addEventListener('change', this.pdfBytesModifiedListener);
                field.addEventListener('blur', this.pdfBytesModifiedListener);
                this.pdfEventListeners.push({ field: field, pdfBytesModifiedListener: this.pdfBytesModifiedListener });
            });
        },
        removePdfEventListeners() {
            const iframe = document.getElementById('iframesection');
            if (iframe) {
                iframe.contentWindow['PDFViewerApplication'].eventBus.off('annotationeditorparamschanged', this.pdfBytesModifiedListener);
                iframe.contentWindow['PDFViewerApplication'].eventBus.off('annotationlayerrendered', this.pdfBytesModifiedListener);
                iframe.contentWindow['PDFViewerApplication'].eventBus.off('editingaction', this.pdfBytesModifiedListener);
            }

            this.pdfEventListeners.forEach(({ field, pdfBytesModifiedListener }) => {
                field.removeEventListener('input', pdfBytesModifiedListener);
                field.removeEventListener('change', pdfBytesModifiedListener);
                field.removeEventListener('blur', pdfBytesModifiedListener);
            });
            this.pdfEventListeners = [];
        },
        async saveFileDocument() {
            if (!this.pdfModifiedFileBytes) {
                return;
            }
            const modifiedFileBytes = await document.getElementById(this.iframeId).contentWindow['PDFViewerApplication'].pdfDocument.saveDocument();
            this.loading = true;
            this.$emit('document-file-updated', modifiedFileBytes);
        },
        setLoading(value) {
            this.loading = value;
        }
    },
    beforeDestroy() {
        this.removePdfEventListeners();
    },
};
</script>
<style scoped>
.save-file-btn {
    position: fixed;
    bottom: 12vh;
    right: 2vw;
    z-index: 100;
}
.pdf-viewer-iframe-wrapper.pdf-viewer-resizing iframe {
    pointer-events: none;
    opacity: 0.3;
    transition: opacity 0.2s;
    height: 0px !important;
    width: 0px !important;
}
.pdf-viewer-resizing {
    background-color: rgb(42 42 46);
    width: 100% !important;
    display: flex;
    justify-content: center;
    align-items: center;
}
</style>
